import React, { useCallback, useMemo, useRef, useState } from "react";
import { Dimensions, Pressable, StyleProp, StyleSheet, Text, View, ViewStyle } from "react-native";
import { Portal } from "react-native-paper";

import { colors, radii, spacing, typographyScale, webTransitions } from "../../../../styles/design-system";

export type PopoverMenuItem = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onPress: () => void;
  danger?: boolean;
  disabled?: boolean;
};

export type PopoverMenuPlacement = "top-start" | "bottom-end";

type PressableStyle = StyleProp<ViewStyle> | ((state: { pressed: boolean; hovered?: boolean }) => StyleProp<ViewStyle>);

interface PopoverMenuProps {
  items: PopoverMenuItem[];
  renderTrigger: (state: { pressed: boolean; hovered?: boolean }) => React.ReactNode;
  triggerAccessibilityLabel: string;
  triggerStyle?: PressableStyle;
  menuWidth?: number;
  offset?: number;
  placement?: PopoverMenuPlacement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(min, value), max);

export const PopoverMenu = ({
  items,
  renderTrigger,
  triggerAccessibilityLabel,
  triggerStyle,
  menuWidth = 200,
  offset = 4,
  placement = "bottom-end",
  open: controlledOpen,
  onOpenChange,
}: PopoverMenuProps) => {
  const triggerRef = useRef<View | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const isControlled = controlledOpen != null;
  const open = isControlled ? (controlledOpen as boolean) : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const estimatedMenuHeight = useMemo(() => {
    // Padding + per-item height estimate; keeps positioning stable without layout measuring.
    const itemHeight = 34;
    const padding = 8;
    return padding + items.length * itemHeight;
  }, [items.length]);

  const openMenuAtTrigger = useCallback(() => {
    if (!triggerRef.current) return;
    triggerRef.current.measureInWindow((x, y, width, height) => {
      const { width: windowWidth, height: windowHeight } = Dimensions.get("window");
      const margin = 8;

      const desired = (() => {
        if (placement === "top-start") {
          // bottom-left of menu sits on top of trigger
          return { left: x, top: y - estimatedMenuHeight - offset };
        }
        // bottom-end: top-right of menu sits on bottom-right of trigger
        return { left: x + width - menuWidth, top: y + height + offset };
      })();

      const left = clamp(desired.left, margin, Math.max(margin, windowWidth - menuWidth - margin));
      const top = clamp(desired.top, margin, Math.max(margin, windowHeight - estimatedMenuHeight - margin));

      setMenuPosition({ left, top });
      setOpen(true);
    });
  }, [estimatedMenuHeight, menuWidth, offset, placement, setOpen]);

  const handleToggle = useCallback(() => {
    if (open) {
      setOpen(false);
      return;
    }
    openMenuAtTrigger();
  }, [open, openMenuAtTrigger, setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleItemPress = useCallback(
    (item: PopoverMenuItem) => {
      if (item.disabled) return;
      setOpen(false);
      item.onPress();
    },
    [setOpen]
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <Pressable
        ref={triggerRef}
        style={triggerStyle ?? styles.trigger}
        onPress={handleToggle}
        accessibilityRole="button"
        accessibilityLabel={triggerAccessibilityLabel}>
        {(state) => renderTrigger(state as { pressed: boolean; hovered?: boolean })}
      </Pressable>
      {open && (
        <Portal>
          <Pressable style={styles.overlay} onPress={handleClose} accessibilityLabel="Close menu" />
          <View style={[styles.menu, { top: menuPosition.top, left: menuPosition.left, width: menuWidth }]}>
            {items.map((item) => (
              <Pressable
                key={item.key}
                disabled={item.disabled}
                style={(state) => {
                  const hovered = (state as { hovered?: boolean }).hovered ?? false;
                  return [styles.menuItem, hovered && styles.menuItemHovered, item.disabled && styles.menuItemDisabled];
                }}
                onPress={() => handleItemPress(item)}
                accessibilityRole="button"
                accessibilityLabel={item.label}>
                {item.icon}
                <Text style={[styles.menuItemText, item.danger && styles.menuItemTextDanger]}>{item.label}</Text>
              </Pressable>
            ))}
          </View>
        </Portal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
    flexShrink: 0,
  },
  trigger: {
    padding: 6,
    borderRadius: radii.xs,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  overlay: {
    // @ts-expect-error - works for web
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
    backgroundColor: "transparent",
  },
  menu: {
    position: "absolute",
    backgroundColor: colors.white,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.grey[200],
    paddingVertical: 4,
    paddingHorizontal: 4,
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    zIndex: 10000,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: radii.xs,
    ...webTransitions.fast,
  },
  menuItemHovered: {
    backgroundColor: colors.grey[50],
  },
  menuItemDisabled: {
    opacity: 0.5,
  },
  menuItemText: {
    fontSize: typographyScale.textSm.fontSize,
    fontFamily: "Inter",
    color: colors.grey[900],
  },
  menuItemTextDanger: {
    color: colors.error[600],
  },
});
